import React, { useState, useContext, useEffect } from "react";
import { Button, Card, TextField, Typography, CircularProgress, Alert, Container, Box } from "@mui/material";

import Nav from "../../components/Nav/Nav";
import { UserContext } from "../../App";
import { UserApiExtended } from "../../api/UserApi";
import AuthApi from "../../api/AuthApi";

import "./UserSettingsView.scss";
import { FullUserDto } from "../../interfaces/UserInterfaces";

const UserSettingsView = () => {
  const user = useContext(UserContext);

  const [fu, setFu] = useState<FullUserDto | null>(null);
  
  // Form state
  const [firstName, setFirstName] = useState(user?.User_FirstName || '');
  const [middleName, setMiddleName] = useState(user?.User_MiddleName || '');
  const [lastName, setLastName] = useState(user?.User_LastName || '');
  const [email, setEmail] = useState('');
  const [dateOfBirth, setDateOfBirth] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  
  // UI state
  const [isLoading, setIsLoading] = useState(false);
  const [isPasswordResetLoading, setIsPasswordResetLoading] = useState(false);
  const [isFetchingUserData, setIsFetchingUserData] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [passwordResetMessage, setPasswordResetMessage] = useState('');

  const applyUserToForm = (fullUserData: FullUserDto) => {
    setFu(fullUserData);
    setFirstName(fullUserData.User_FirstName || '');
    setMiddleName(fullUserData.User_MiddleName || '');
    setLastName(fullUserData.User_LastName || '');
    setEmail(fullUserData.User_Email || '');
    if (fullUserData.User_DOB) {
      const dobDate = new Date(fullUserData.User_DOB);
      setDateOfBirth(dobDate.toISOString().split('T')[0]);
    } else {
      setDateOfBirth('');
    }
    setPhoneNumber(fullUserData.User_PhoneNumber ?? '');
  };

  // Initialize form with user data when component mounts or user changes
  useEffect(() => {
    const initializeUserData = async () => {
      if (user) {
        // Try to fetch full user data for email and ensure DOB is current
        try {
          setIsFetchingUserData(true);
          
          const fullUserDataGuard = (obj: any): obj is FullUserDto => {
            return obj && typeof obj === 'object' && 'User_guid' in obj;
          }
          const fullUserData = await AuthApi.me();
          if (!fullUserDataGuard(fullUserData)) {
            throw new Error('Invalid user data');
          }

          applyUserToForm(fullUserData);
          
        } catch (error) {
          console.error('Failed to fetch full user data:', error);
          // Fallback to basic user data if full data fetch fails
          setFirstName(user.User_FirstName || '');
          setLastName(user.User_LastName || '');
          
          if (user.User_DOB) {
            const dobDate = new Date(user.User_DOB);
            const formattedDOB = dobDate.toISOString().split('T')[0];
            setDateOfBirth(formattedDOB);
          }
        } finally {
          setIsFetchingUserData(false);
        }
      }
    };

    initializeUserData();
  }, [user]);

  const controlsChanged = () => {
    const formattedStoredDob = fu?.User_DOB
      ? new Date(fu.User_DOB).toISOString().split('T')[0]
      : '';
    return (
      firstName !== fu?.User_FirstName ||
      middleName !== fu?.User_MiddleName ||
      lastName !== fu?.User_LastName ||
      email !== fu?.User_Email ||
      dateOfBirth !== formattedStoredDob ||
      phoneNumber !== (fu?.User_PhoneNumber ?? '')
    );
  }

  const handleUpdateProfile = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) return;

    setIsLoading(true);
    setSuccessMessage('');
    setErrorMessage('');

    try {
      const updateData: any = {};
      
      if (firstName.trim() !== (fu?.User_FirstName ?? '')) {
        updateData.User_FirstName = firstName.trim();
      }
      
      if (middleName.trim() !== (fu?.User_MiddleName || '')) {
        updateData.User_MiddleName = middleName.trim();
      }
      
      if (lastName.trim() !== (fu?.User_LastName ?? '')) {
        updateData.User_LastName = lastName.trim();
      }
      
      if (email.trim() !== (fu?.User_Email ?? '')) {
        updateData.User_Email = email.trim();
      }
      
      const formattedStoredDob = fu?.User_DOB
        ? new Date(fu.User_DOB).toISOString().split('T')[0]
        : '';
      if (dateOfBirth && dateOfBirth !== formattedStoredDob) {
        updateData.User_DOB = dateOfBirth;
      }

      if (phoneNumber.trim() !== (fu?.User_PhoneNumber ?? '')) {
        updateData.User_PhoneNumber = phoneNumber.trim();
      }

      // Only make API call if there are changes
      if (Object.keys(updateData).length > 0) {
        const updatedUser = await UserApiExtended.updateUser(user.User_guid, updateData);
        
        applyUserToForm(updatedUser);

        // Update local storage with new user data
        const currentUserData = JSON.parse(localStorage.getItem('user') || '{}');
        const newUserData = { ...currentUserData, ...updatedUser };
        localStorage.setItem('user', JSON.stringify(newUserData));
        
        setSuccessMessage('Profile updated successfully!');
      } else {
        setErrorMessage('No changes to update.');
      }
    } catch (error) {
      setErrorMessage(error instanceof Error ? error.message : 'Failed to update profile');
    } finally {
      setIsLoading(false);
    }
  };

  const handlePasswordReset = async () => {
    if (!email.trim()) {
      setErrorMessage('Please enter your email address to reset your password.');
      return;
    }

    setIsPasswordResetLoading(true);
    setPasswordResetMessage('');
    setErrorMessage('');

    try {
      const message = await UserApiExtended.requestPasswordReset(email.trim());
      setPasswordResetMessage(message);
    } catch (error) {
      setErrorMessage(error instanceof Error ? error.message : 'Failed to send password reset email');
    } finally {
      setIsPasswordResetLoading(false);
    }
  };

  if (!user) {
    return (
      <>
        <Nav />
        <Container maxWidth="md">
          <Box sx={{ mt: 4, textAlign: 'center' }}>
            <Typography variant="h6">Please log in to view your settings.</Typography>
          </Box>
        </Container>
      </>
    );
  }

  return (
    <>
      <Nav />
      <Container maxWidth="md" className="user-settings-view">
        <Box sx={{ mt: 4, mb: 4 }}>
          <Typography variant="h4" gutterBottom>
            Account Settings
          </Typography>
          <Typography variant="body1" color="text.secondary" gutterBottom>
            Update your profile information and manage your account settings.
          </Typography>

          {successMessage && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {successMessage}
            </Alert>
          )}

          {errorMessage && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {errorMessage}
            </Alert>
          )}

          {passwordResetMessage && (
            <Alert severity="info" sx={{ mb: 2 }}>
              {passwordResetMessage}
            </Alert>
          )}

          <Card sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              Profile Information
            </Typography>
            
            <Box component="form" onSubmit={handleUpdateProfile}>
              <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>                
                <TextField
                  label="First Name"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  fullWidth
                  required
                  inputProps={{ maxLength: 32 }}
                />
                <TextField
                  label="Middle Name"
                  value={middleName}
                  onChange={(e) => setMiddleName(e.target.value)}
                  fullWidth
                  inputProps={{ maxLength: 32 }}
                  helperText="Optional"
                />
                <TextField
                  label="Last Name"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  fullWidth
                  required
                  inputProps={{ maxLength: 32 }}
                />
              </Box>              <TextField
                label="Email Address"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                fullWidth
                sx={{ mb: 2 }}
                helperText={isFetchingUserData ? "Loading current email..." : null}
                disabled={isFetchingUserData}
              />

              <TextField
                label="Date of Birth"
                type="date"
                value={dateOfBirth}
                onChange={(e) => setDateOfBirth(e.target.value)}
                fullWidth
                required
                sx={{ mb: 2 }}
                InputLabelProps={{
                  shrink: true,
                }}
                helperText="Select your date of birth"
              />

              <TextField
                label="Phone Number"
                type="tel"
                value={phoneNumber}
                onChange={(e) => setPhoneNumber(e.target.value)}
                fullWidth
                required
                sx={{ mb: 3 }}
                placeholder="(555) 123-4567"
              />

              <Button
                type="submit"
                variant="contained"
                disabled={isLoading || !controlsChanged()}
                sx={{ mr: 2 }}
              >
                {isLoading ? <CircularProgress size={24} /> : 'Update Profile'}
              </Button>
            </Box>
          </Card>

          <Card sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Password Reset
            </Typography>

            <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
              <Button
                variant="outlined"
                onClick={handlePasswordReset}
              >
                {isPasswordResetLoading ? <CircularProgress size={24} /> : 'Send Reset Email'}
              </Button>
            </Box>
          </Card>
        </Box>
      </Container>
    </>
  );
};

export default UserSettingsView;